import { isDefined, isNil } from "@clipboard-health/util-ts";
import { patch } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { invalidateBookabilityStatus } from "@src/appV2/OpenShifts/ShiftAction/api/useBookabilityStatus";
import { invalidateWorkerRequirements } from "@src/appV2/redesign/Documents/api/invalidateWorkerRequirements";
import { HelloSignWorkflowSessionStatus } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/constants";
import {
  type HelloSignSessionResponse,
  helloSignSessionResponseSchema,
} from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const updateHelloSignSessionAttributes = z
  .object({
    status: z.union([
      z.literal(HelloSignWorkflowSessionStatus.ERRORED),
      z.literal(HelloSignWorkflowSessionStatus.SIGNED),
    ]),
    errorMessage: z.string().optional(),
  })
  .refine(
    (data) => data.status !== HelloSignWorkflowSessionStatus.SIGNED || isNil(data.errorMessage),
    {
      message: "errorMessage must not be present when status is SIGNED",
      path: ["errorMessage"],
    }
  );

type UpdateHelloSignSessionAttributes = z.infer<typeof updateHelloSignSessionAttributes>;

const updateHelloSignSessionSchema = z.object({
  data: z.object({
    attributes: updateHelloSignSessionAttributes,
    type: z.literal(API_TYPES.helloSignSession),
  }),
});

interface UseUpdateHelloSignSessionProps {
  helloSignSessionId?: string;
  requirementId?: string;
  workerId?: string;
  include?: "documents";
}

export function useUpdateHelloSignSession(
  props: UseUpdateHelloSignSessionProps,
  options: UseMutationOptions<
    HelloSignSessionResponse,
    AxiosError,
    UpdateHelloSignSessionAttributes
  > = {}
): UseMutationResult<HelloSignSessionResponse, AxiosError, UpdateHelloSignSessionAttributes> {
  const { helloSignSessionId, requirementId, workerId, include } = props;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attributes: UpdateHelloSignSessionAttributes) => {
      if (isNil(workerId) || isNil(requirementId) || isNil(helloSignSessionId)) {
        throw new Error("Missing required parameters");
      }

      const response = await patch({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/workers/${workerId}/requirements/${requirementId}/hello-sign-sessions/${helloSignSessionId}`,
        queryParams: isDefined(include) ? { include } : undefined,
        data: {
          data: {
            attributes,
            type: API_TYPES.helloSignSession,
          },
        },
        requestSchema: updateHelloSignSessionSchema,
        responseSchema: helloSignSessionResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_HELLO_SIGN_SESSION_FAILURE,
    },
    onSuccess: async (data, variables, context) => {
      await Promise.all([
        invalidateBookabilityStatus(queryClient),
        invalidateWorkerRequirements(queryClient),
        options.onSuccess?.(data, variables, context),
      ]);
    },
    ...options,
  });
}
